/* eslint-disable jsx-a11y/anchor-is-valid */
import * as React from 'react';
import AddedFilter from './AddedFilter';
import SavedFilters from './SavedFilters';
import classnames from 'classnames';
import { useOutsideClick } from '../../core/hooks';
import { IListFilterProps } from './props';
import { Dict } from '../../models';
import SaveFilter from './SaveFilter';
import {
  FilterValueType,
  SavedFilter,
} from '../../core/api/models/saved-filters';
// import { AppPermissions } from '../../api/models/permissions';
import { translate } from '../../utils/text-utils';

interface IListFiltersProps {
  filters?: Dict<FilterValueType>;
  onFilter?: (filters: Dict<FilterValueType>) => void;
  savedFilters?: SavedFilter[];
  onSaveFilter: (name: string) => void;
  isSavingFilter?: boolean;
  onRemoveFilter?: (savedFilterId: string) => void;
  ignoreAddedFilters?: string[];
  hideSearch?: boolean;
}

export const ListFilters: React.FunctionComponent<IListFiltersProps> = (
  props
) => {
  //   const canSaveFilter = useHasPermission(AppPermissions.SaveFilter);
  //   const canRemoveFilter = useHasPermission(AppPermissions.RemoveFilter);
  const [isOpen, setIsOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const ref = React.useRef<HTMLUListElement>(null);
  const filterRef = React.useRef<HTMLUListElement>(null);

  const [selectedFilter, setSelectedFilter] = React.useState<string>();
  const [filters, setFilters] = React.useState<Dict<FilterValueType>>({
    ...props.filters,
  });

  useOutsideClick(ref, () => {
    setIsOpen(false);
    setSearch('');
    setSelectedFilter(undefined);
  });

  React.useEffect(() => {
    setFilters({ ...props.filters });
  }, [props.filters]);

  const setFilterValue = (key: string, value: FilterValueType) => {
    if (!value) {
      resetFilterValue(key);
    } else {
      const newFilters = {
        ...filters,
        [key]: value,
      };
      setFilters(newFilters);
      setIsOpen(false);
      setSelectedFilter(undefined);
      props.onFilter?.(newFilters);
    }
  };

  const resetFilterValue = (key: string) => {
    const newFilters = { ...filters };
    delete newFilters[key];
    setFilters(newFilters);
    setIsOpen(false);
    setSelectedFilter(undefined);
    props.onFilter?.(newFilters);
  };

  const resetFilters = (val: Dict<FilterValueType>) => {
    const newFilters = { ...val };
    setFilters(newFilters);
    setIsOpen(false);
    setSelectedFilter(undefined);
    props.onFilter?.(newFilters);
  };

  const allFilters = React.Children.map(props.children, (child) => {
    if (React.isValidElement(child)) {
      return child;
    }
  })?.filter((child) => !!child);

  if (!allFilters?.length) {
    return <></>;
  }

  const childFilters = search
    ? allFilters.filter((child) =>
        child.props.label?.match(new RegExp(search, 'i'))
      )
    : allFilters;

  const appliedFiltersCount = Object.keys(filters).reduce(
    (count, filterKey) => {
      if (!filterKey) {
        return count;
      }
      const filterValue = filters[filterKey];
      if (!filterValue) {
        return count;
      }
      if (Array.isArray(filterValue)) {
        return count + filterValue.length;
      } else {
        return count + 1;
      }
    },
    0
  );
  const renderHeader = () => (
    <div className="font-xs d-flex align-items-center mr-3 py-2 pr-1">
      <div className="py-1">
        <i className="icon-tool font-sm text-primary"></i>
        <span className="ml-2">
          {translate('common.filter')}:
          <span className="font-weight-bold text-uppercase ml-1">
            {translate('common.all')}
          </span>
        </span>
      </div>
    </div>
  );

  const getAddedFilters = () => {
    const adFilters: React.ReactNode[] = [];
    Object.keys(filters).forEach((filterKey) => {
      const matchingFilter = childFilters.find(
        (child) => child.props.filterKey === filterKey
      );
      if (!filterKey || props.ignoreAddedFilters?.includes(filterKey)) {
        return <></>;
      }
      const filterValue = filters[filterKey];
      if (!filterValue || matchingFilter?.props?.fixed) {
        return <></>;
      }
      if (Array.isArray(filterValue)) {
        let filterValues = filterValue
          ?.map((f) => f.label)
          ?.slice(0, 2)
          ?.join(', ');
        if (filterValue.length > 2) {
          filterValues = `${filterValues}, + ${filterValue.length - 2} more`;
        }

        adFilters.push(
          <AddedFilter
            key={`filter-${filterKey}-${filterValues}`}
            name={matchingFilter?.props?.label || 'Filter'}
            valueLabel={filterValues}
            onRemove={() => resetFilterValue(filterKey)}
            onClick={() => {
              setIsOpen(true);
              setSelectedFilter(filterKey);
              filterRef?.current?.scrollIntoView();
            }}
          />
        );
      } else {
        adFilters.push(
          <AddedFilter
            name={matchingFilter?.props?.label || 'Filter'}
            valueLabel={filterValue.label}
            onRemove={() => resetFilterValue(filterKey)}
            onClick={() => {
              setIsOpen(true);
              setSelectedFilter(filterKey);
              filterRef?.current?.scrollIntoView();
            }}
          />
        );
      }
    });

    return adFilters;
  };

  const renderFixedFilters = () => {
    return childFilters.map((child, index) => {
      const childProps: IListFilterProps = child.props;
      if (!childProps.fixed) {
        return <></>;
      }

      return React.cloneElement(child, {
        ...childProps,
        applyFilter: (value: FilterValueType) =>
          setFilterValue(childProps.filterKey, value),
        resetFilter: () => resetFilterValue(childProps.filterKey),
        onClose: () => setSelectedFilter(undefined),
        value: filters[childProps.filterKey],
      });
    });
  };

  const renderSearch = () => {
    return (
      <div className="search">
        <div className="input-group border-bottom">
          <input
            type="text"
            className="form-control border-0 pr-0 font-md"
            placeholder={translate('Home.Search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="input-group-append">
            <span className="form-control-search btn btn-link border-0 bg-transparent mr-1" />
          </div>
        </div>
      </div>
    );
  };

  const renderFilterSaveOptions = () => {
    if (!appliedFiltersCount) {
      return <></>;
    }
    return (
      <div className="row gutter-8">
        {/* {props.onSaveFilter && canSaveFilter && ( */}
        <div className="col-auto">
          <SaveFilter
            onSave={props.onSaveFilter}
            isSaving={props.isSavingFilter}
          />
        </div>
        {/* )} */}
        {/* {canRemoveFilter && ( */}
        <div className="col-auto">
          <span
            className="cursor-pointer d-inline-block text-gray-500 text-center text-uppercase font-xxxxs font-weight-semibold py-2"
            onClick={() => resetFilters({})}
          >
            <i className="icon-reset font-base d-block mb-1"></i>
            {translate('orders.Reset')}
          </span>
        </div>
        {/* )} */}
      </div>
    );
  };

  const addedFilters = getAddedFilters();

  const hasFixedFilters =
    childFilters.filter((child, index) => {
      const childProps: IListFilterProps = child.props;
      return childProps.fixed && filters[childProps.filterKey];
    }).length > 0;

  const hasAddedFilters = addedFilters.length > 0;

  return (
    <div
      className={classnames({
        'px-lg-4 px-3 filter-blk line-height-normal': true,
        'filter-selected': hasFixedFilters || hasAddedFilters,
      })}
    >
      <div className="row align-items-center">
        <div className="col">
          <div className="d-flex align-items-start">
            {renderHeader()}
            <ul
              className="filter-choices row gutter-5 align-items-center list-unstyled p-0 mb-0"
              ref={ref}
            >
              {renderFixedFilters()}
              {addedFilters}
              <li className="col-auto filter-add">
                <div
                  className={classnames({
                    'dropdown dropdown-add-filter': true,
                    in: isOpen,
                  })}
                >
                  <span
                    className="dropdown-click cursor-pointer text-uppercase font-xs font-weight-semibold py-2 d-inline-block text-primary font-weight-medium"
                    onClick={() => {
                      setIsOpen(!isOpen);
                      if (isOpen) {
                        setSearch('');
                        setSelectedFilter(undefined);
                      }
                    }}
                  >
                    <span className="d-inline-flex pt-px mt-1">
                      {translate('orders.Add_Filter')}
                    </span>
                  </span>
                  <div
                    className={classnames({
                      'dropdown-menu shadow font-md font-weight-medium border w-100 p-0 custom-scroll-vertical ':
                        true,
                      in: isOpen,
                    })}
                  >
                    <div className="position-relative">
                      {/* {selectedFilter === undefined &&
                        !props.hideSearch &&
                        renderSearch()} */}
                      <SavedFilters
                        items={props.savedFilters}
                        onRemove={props.onRemoveFilter}
                        onSelect={resetFilters}
                      />
                      <ul
                        className={classnames({
                          'list-unstyled m-0 font-weight-semibold': true,
                          'px-3': selectedFilter === undefined,
                        })}
                        ref={filterRef}
                      >
                        {childFilters.map((child, index) => {
                          const childProps: IListFilterProps = child.props;
                          if (
                            selectedFilter &&
                            selectedFilter !== childProps.filterKey
                          ) {
                            return <></>;
                          }
                          if (childProps.fixed) {
                            return <></>;
                          }

                          if (childProps.noContent) {
                            return React.cloneElement(child, {
                              ...childProps,
                              applyFilter: (value: FilterValueType) =>
                                setFilterValue(childProps.filterKey, value),
                              resetFilter: () =>
                                resetFilterValue(childProps.filterKey),
                              onClose: () => setSelectedFilter(undefined),
                              value: filters[childProps.filterKey],
                            });
                          }

                          return (
                            <li key={index}>
                              <a
                                className={classnames({
                                  'text-gray-700 text-decoration-none cursor-pointer':
                                    true,
                                  'd-block': selectedFilter === undefined,
                                  'd-none': selectedFilter !== undefined,
                                })}
                                onClick={() =>
                                  setSelectedFilter(
                                    selectedFilter === childProps.filterKey
                                      ? undefined
                                      : childProps.filterKey
                                  )
                                }
                              >
                                {childProps.label}
                              </a>
                              <div
                                className={classnames({
                                  'sub-dropdown top-0 left-0 w-100 bg-white zIndex-99':
                                    true,
                                  in: selectedFilter === childProps.filterKey,
                                })}
                              >
                                {React.cloneElement(child, {
                                  ...childProps,
                                  applyFilter: (value: FilterValueType) =>
                                    setFilterValue(childProps.filterKey, value),
                                  resetFilter: () =>
                                    resetFilterValue(childProps.filterKey),
                                  onClose: () => setSelectedFilter(undefined),
                                  value: filters[childProps.filterKey],
                                })}
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>
        {/* <div className="col-auto">{renderFilterSaveOptions()}</div> */}
      </div>
    </div>
  );
};
